/**
 * PromptDialog
 * Cross-platform text input dialog replacing iOS-only Alert.prompt.
 * Uses Modal for overlay. All elements have testIDs for Maestro testing.
 *
 * Keyboard avoidance uses useReanimatedKeyboardAnimation from
 * react-native-keyboard-controller (works inside Modal since v1.13+
 * via the app-level KeyboardProvider — no nested provider needed).
 *
 * The parent screen's ScrollView must have keyboardShouldPersistTaps="handled"
 * so that button taps inside the Modal are not swallowed by keyboard dismissal
 * (see RN issue #28871).
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Modal, Pressable, TextInput, StyleSheet, View, Keyboard, Platform } from 'react-native';
import Animated, { useAnimatedStyle } from 'react-native-reanimated';
import { useReanimatedKeyboardAnimation } from 'react-native-keyboard-controller';
import { Text } from '@gluestack-ui/themed';
import { colors as appColors, FS_SM, FS_MD, FS_LG } from '../../theme';
import { useDisplayTheme } from '../../hooks/useDisplayTheme';

interface PromptDialogProps {
  visible: boolean;
  title: string;
  message?: string;
  placeholder?: string;
  defaultValue?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  maxLength?: number;
  onConfirm: (value: string) => void;
  onCancel: () => void;
}

export function PromptDialog({
  visible,
  title,
  message,
  placeholder,
  defaultValue = '',
  confirmLabel,
  cancelLabel,
  maxLength,
  onConfirm,
  onCancel,
}: PromptDialogProps) {
  const { theme, fontScale, ffs } = useDisplayTheme();
  const [value, setValue] = useState(defaultValue);
  const inputRef = useRef<TextInput>(null);

  const { height } = useReanimatedKeyboardAnimation();

  // height.value goes from 0 → -keyboardHeight; shift the card up by half
  const cardStyle = useAnimatedStyle(() => ({
    transform: [{ translateY: height.value / 2 }],
  }));

  const handleShow = useCallback(() => {
    // Delay focus slightly — Modal's native window needs time to become
    // the key window on iOS before it can accept keyboard input.
    setTimeout(() => inputRef.current?.focus(), 100);
  }, []);

  // Intentional: reset input value when dialog opens
  /* eslint-disable react-hooks/set-state-in-effect */
  useEffect(() => {
    if (visible) setValue(defaultValue);
  }, [visible, defaultValue]);
  /* eslint-enable react-hooks/set-state-in-effect */

  const handleConfirm = () => {
    Keyboard.dismiss();
    const trimmed = value.trim();
    if (trimmed) onConfirm(trimmed);
  };

  const handleCancel = () => {
    Keyboard.dismiss();
    onCancel();
  };

  return (
    <Modal
      visible={visible}
      transparent
      animationType="fade"
      onRequestClose={handleCancel}
      onShow={handleShow}
    >
      <View testID="prompt-dialog-overlay" style={styles.overlay}>
        <Animated.View
          testID="prompt-dialog"
          style={[styles.card, { backgroundColor: theme.card }, cardStyle]}
        >
          <Text
            testID="prompt-dialog-title"
            allowFontScaling={false}
            style={[styles.title, { fontSize: FS_LG * fontScale, color: theme.text, ...ffs }]}
          >
            {title}
          </Text>
          {message && (
            <Text
              testID="prompt-dialog-message"
              allowFontScaling={false}
              style={[styles.message, { fontSize: FS_SM * fontScale, color: theme.textSecondary, ...ffs }]}
            >
              {message}
            </Text>
          )}
          <TextInput
            ref={inputRef}
            testID="prompt-dialog-input"
            accessibilityLabel={title}
            value={value}
            onChangeText={setValue}
            placeholder={placeholder}
            placeholderTextColor={theme.textSecondary}
            maxLength={maxLength}
            selectTextOnFocus
            style={[
              styles.input,
              {
                fontSize: FS_MD * fontScale,
                color: theme.text,
                borderColor: theme.border,
                backgroundColor: theme.background,
                ...ffs,
              },
            ]}
          />
          <View style={styles.buttons}>
            <Pressable
              testID="prompt-dialog-cancel"
              accessibilityRole="button"
              accessibilityLabel={cancelLabel || 'Cancel'}
              onPress={handleCancel}
              style={[styles.button, { borderColor: theme.border, borderWidth: 1 }]}
            >
              <Text
                allowFontScaling={false}
                numberOfLines={1}
                adjustsFontSizeToFit
                style={[styles.buttonText, { fontSize: FS_SM * fontScale, color: theme.text, ...ffs }]}
              >
                {cancelLabel || 'Cancel'}
              </Text>
            </Pressable>
            <Pressable
              testID="prompt-dialog-confirm"
              accessibilityRole="button"
              accessibilityLabel={confirmLabel || 'OK'}
              onPress={handleConfirm}
              style={[styles.button, { backgroundColor: appColors.primary }]}
            >
              <Text
                allowFontScaling={false}
                numberOfLines={1}
                adjustsFontSizeToFit
                style={[styles.buttonText, { fontSize: FS_SM * fontScale, color: '#FFFFFF', fontWeight: '600', ...ffs }]}
              >
                {confirmLabel || 'OK'}
              </Text>
            </Pressable>
          </View>
        </Animated.View>
      </View>
    </Modal>
  );
}

const styles = StyleSheet.create({
  overlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 32,
  },
  card: {
    borderRadius: 16,
    padding: 24,
    width: '100%',
    maxWidth: 340,
  },
  title: {
    fontWeight: '700',
    marginBottom: 8,
    textAlign: 'center',
  },
  message: {
    marginBottom: 16,
    textAlign: 'center',
    lineHeight: 20,
  },
  input: {
    borderWidth: 1,
    borderRadius: 10,
    paddingHorizontal: 12,
    paddingVertical: Platform.OS === 'ios' ? 12 : 8,
    marginBottom: 20,
  },
  buttons: {
    flexDirection: 'row',
    gap: 12,
  },
  button: {
    flex: 1,
    paddingVertical: 12,
    borderRadius: 10,
    alignItems: 'center',
  },
  buttonText: {
    fontWeight: '500',
  },
});
